import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, QrCode, Monitor } from "lucide-react";

const empty = () => ({ name: "", location_id: "", token_validity_seconds: 120, active: false });

export default function QrPanel() {
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  const load = () => api.get("/hr/attendance/qr-configs").then(r => setRows(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/hr/attendance/locations").then(r => setLocations(r.data)).catch(() => {}); }, []);

  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, location_id: c.location_id || "", token_validity_seconds: c.token_validity_seconds, active: c.active }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const v = Math.min(600, Math.max(30, parseInt(form.token_validity_seconds) || 120));
    const body = { ...form, location_id: form.location_id || null, token_validity_seconds: v };
    try {
      if (editing) await api.put(`/hr/attendance/qr-configs/${editing.id}`, body);
      else await api.post("/hr/attendance/qr-configs", body);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete QR configuration?")) return;
    try { await api.delete(`/hr/attendance/qr-configs/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const openDisplay = (id) => window.open(`/attendance-qr?config=${id}`, "_blank");
  const locName = (id) => locations.find(l => l.id === id)?.name || "—";

  return (
    <div data-testid="att-qr-panel">
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 mb-3 text-sm text-sky-800">
        Office QR uses short-lived, server-signed rotating tokens (no reusable secret inside the QR). It ships <b>OFF</b> — a QR only affects a punch when a policy has <b>Require Office QR = ON</b>.
      </div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Leave a screen/tablet on the Display page in the office; staff scan the current code at clock in/out.</p>
        <Button data-testid="att-qr-new" onClick={() => { setEditing(null); setForm(empty()); setOpen(true); }} className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />QR Config</Button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {rows.map(c => (
          <div key={c.id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`att-qr-${c.id}`}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold flex items-center gap-2"><QrCode className="w-4 h-4 text-sky-600" />{c.name}</h4>
              <span className="flex gap-1">
                <button onClick={() => openEdit(c)} data-testid={`att-qr-edit-${c.id}`}><Pencil className="w-4 h-4 text-slate-400" /></button>
                <button onClick={() => del(c.id)}><Trash2 className="w-4 h-4 text-rose-500" /></button>
              </span>
            </div>
            <div className="text-xs text-slate-500">Location: {locName(c.location_id)}</div>
            <div className="flex items-center gap-2 mt-1.5">
              <Badge variant="outline">token {c.token_validity_seconds}s</Badge>
              <Badge variant="outline" className={c.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-400"}>{c.active ? "Active" : "Inactive"}</Badge>
            </div>
            <Button size="sm" variant="outline" className="mt-3 w-full" data-testid={`att-qr-display-${c.id}`} onClick={() => openDisplay(c.id)} disabled={!c.active}><Monitor className="w-4 h-4 mr-1.5" />Open Display</Button>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-2 rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-slate-400 text-sm">No QR configurations yet</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="att-qr-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit QR configuration" : "New QR configuration"}</DialogTitle>
            <DialogDescription>Configuration is separate from Attendance Policy. Enabling here does NOT force QR on staff — that is controlled per policy.</DialogDescription></DialogHeader>
          <div><Label>QR Name *</Label><Input data-testid="att-qr-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="FASFONE Warehouse Attendance" /></div>
          <div><Label>Linked Attendance Location</Label>
            <Select value={form.location_id || "none"} onValueChange={v => setForm({ ...form, location_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="att-qr-location"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Token Validity (seconds, 30–600)</Label><Input type="number" data-testid="att-qr-validity" value={form.token_validity_seconds} onChange={e => setForm({ ...form, token_validity_seconds: e.target.value })} /></div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} data-testid="att-qr-active" />Active</label>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="att-qr-save" className="bg-slate-900">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
